import {
  GetNotificationStatusDocument,
  ArtboardUnreadFragmentDoc,
  CommentInfoFragment,
  CommentInfoFragmentDoc,
  useUpdateCommentsMutation,
} from 'gqlTypes'

import { useThreadRouteParams } from 'utils/hooks/useThreadRouteParams'

import { dataIdFromObject } from 'graphql/apolloClient'
import * as Sentry from '@sentry/browser'

interface MarkCommentsOrRepliesAsRead {
  permanentArtboardShortId: string
  identifiers: string[]
}

/**
 * Hook to mark a comment as read, returns a function expecting
 * the identifiers to mark as read, this hooks also updates the cache if needed
 */
export const useMarkCommentsAsRead = () => {
  const { isThreadRoute } = useThreadRouteParams()

  const [markAsRead] = useUpdateCommentsMutation({
    redirectErrors: false,
    onError: 'unsafe-throw-exception',
    refetchQueries: [
      {
        query: GetNotificationStatusDocument,
      },
    ],
  })

  const markCommentsOrRepliesAsRead = ({
    permanentArtboardShortId,
    identifiers,
  }: MarkCommentsOrRepliesAsRead) =>
    markAsRead({
      variables: {
        identifiers,
        input: {
          isRead: true,
        },
      },
      update: cache => {
        try {
          // Resets unreadCount from ArtboardBase in these 2 scenarios:
          // - when mounting Artboard detail
          // - when getting new comments in Artboard detail page
          const shortId = permanentArtboardShortId

          if (!isThreadRoute) {
            cache.writeFragment({
              id: dataIdFromObject({
                __typename: 'ArtboardBase',
                shortId,
              }) as string,
              fragment: ArtboardUnreadFragmentDoc,
              data: {
                __typename: 'ArtboardBase',
                shortId,
                unreadCount: 0,
              },
            })
          }

          // Update cache for every comment/reply marked as read
          identifiers.forEach(commentIdentifier => {
            const fragmentId = dataIdFromObject({
              __typename: 'Comment',
              id: commentIdentifier,
            }) as string

            const commentToUpdate = cache.readFragment({
              fragment: CommentInfoFragmentDoc,
              fragmentName: 'CommentInfo',
              id: fragmentId,
            }) as CommentInfoFragment

            cache.writeFragment<CommentInfoFragment>({
              fragment: CommentInfoFragmentDoc,
              fragmentName: 'CommentInfo',
              id: fragmentId,
              data: { ...commentToUpdate, isRead: true },
            })
          })
        } catch (e) {
          Sentry.captureException(e)
        }
      },
    })

  return markCommentsOrRepliesAsRead
}
